import React, { useEffect, useState } from "react";
import HomeModal from "../Modal";
import { useDispatch, useSelector } from "react-redux";
import { getJobsAction } from "../../../../redux/actions/jobActions";
import { SectionLoader } from "../../../../components/PageLoader";
import { useHistory, useParams, useLocation } from "react-router-dom";
import CustomButton from "../../../../components/CustomButton";
import { ButtonLoader } from "../../../../components/ButtonLoader";
import { saveBusinessRef } from "../../../../redux/actions/businessActions";

import JobCard from "./JobCard";
import JobFilters from "./JobFilters";

const EMPTY_FILTERS = {
  jobTier: "",
  datePosted: "",
  jobType: [],
  experienceLevel: [],
  city: "",
  countryId: "",
  region: "",
  skills: [],
};

const countActiveFilters = (filters) => {
  let count = 0;
  if (filters.jobTier) count++;
  if (filters.datePosted) count++;
  if (filters.jobType.length > 0) count++;
  if (filters.experienceLevel.length > 0) count++;
  if (filters.city) count++;
  if (filters.countryId) count++;
  if (filters.region) count++;
  if (filters.skills.length > 0) count++;
  return count;
};

const AllJobs = () => {
  const [page, setPage] = useState(1);
  const history = useHistory();
  const [totalJobs, setTotalJobs] = useState(0);
  const { pathname } = useLocation();
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("details");
  const { jobId, refLink } = useParams();
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(EMPTY_FILTERS);
  const jobsPerPage = 20;

  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.auth);
  const { jobsData, locationFilter } = useSelector(({ job }) => job);
  const { countries } = useSelector(({ country }) => country);
  const jobs = jobsData && jobsData.jobs;

  // Total pages driven purely by backend count
  const totalPages = Math.max(1, Math.ceil(totalJobs / jobsPerPage));
  const activeFilterCount = countActiveFilters(activeFilters);

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(page === 1);
      setLoadingMore(page > 1);

      try {
        // Modal filters (countryId/region) override the toolbar locationFilter
        const effectiveLocationFilter =
          activeFilters.countryId || activeFilters.region ? null : locationFilter;

        const response = await getJobsAction(
          dispatch,
          page,
          jobsPerPage,
          activeFilters,
          effectiveLocationFilter
        );

        if (response && response.total) {
          setTotalJobs(response.total);
        } else {
          setTotalJobs(0);
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      }

      setLoading(false);
      setLoadingMore(false);
    };

    fetchJobs();
  }, [dispatch, page, jobsPerPage, activeFilters, locationFilter]);

  useEffect(() => {
    setPage(1);
  }, [locationFilter]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const saveForLater = () => {
    const payload = {
      job_id: jobId,
      status: 1,
      refCode: refLink ? refLink : null,
    };
    const saveLater = async () => {
      await saveBusinessRef(dispatch, page, payload);
    };
    saveLater();
  };

  const handleApplyFilters = (newFilters) => {
    setActiveFilters(newFilters);
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setActiveFilters(EMPTY_FILTERS);
    setPage(1);
  };

  return (
    <div className="row position-relative mt-3 align-items-stretch">
      {jobId && (
        <HomeModal
          setSection={setSection}
          section={section}
          open={true}
          handleClose={() => history.push("/home")}
          isBusiness={pathname.includes("biz") || pathname.includes("coy")}
          isFromCompany={!pathname.includes("coy")}
          saveForLater={saveForLater}
        />
      )}

      {userData?.country == "000" ? (
        <div
          className="col-12 d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "400px", padding: "40px 20px" }}
        >
          <div
            className="d-flex flex-column align-items-center justify-content-center text-center"
            style={{
              maxWidth: "600px",
              backgroundColor: "#f8f9fa",
              borderRadius: "16px",
              padding: "48px 32px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.07)",
            }}
          >
            <div style={{ fontSize: "64px", marginBottom: "24px", opacity: 0.8 }}>🌍</div>
            <h3 style={{ fontSize: "24px", fontWeight: "600", color: "#2c3e50", marginBottom: "16px", lineHeight: "1.4" }}>
              Your country's hiring scene starts with you!

            </h3>
            <p style={{ fontSize: "16px", color: "#6c757d", lineHeight: "1.6", marginBottom: "0" }}>
              Be the first to post a job and put your country on the map. Whether you're building a team or exploring new opportunities, workbrook has everything you need to get started. Browse remote roles, discover talent across locations, or  {" "}
              <a href="/onboarding" style={{ color: "#0d6efd" }}>
                Visit our onboarding page
              </a>{" "}
        to see how workbrook can work for you.
            </p>
          </div>
        </div>
      ) : null}

      {jobs && (
        <>
          {/* ── Filter Bar ── */}
          <div className="col-12 mb-3">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <button
                onClick={() => setFilterModalOpen(true)}
                disabled={loading || loadingMore}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "9px 18px",
                  borderRadius: "20px",
                  border: activeFilterCount > 0 ? "2px solid #3A6ED4" : "1px solid #ddd",
                  backgroundColor: activeFilterCount > 0 ? "#E8F0FE" : "#fff",
                  color: activeFilterCount > 0 ? "#3A6ED4" : "#333",
                  fontSize: "14px",
                  fontWeight: activeFilterCount > 0 ? "600" : "400",
                  cursor: loading || loadingMore ? "not-allowed" : "pointer",
                  opacity: loading || loadingMore ? 0.75 : 1,
                }}
              >
                {loading || loadingMore ? (
                  <span
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid currentColor",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "wb-spin 0.7s linear infinite",
                    }}
                  />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                    <line x1="11" y1="18" x2="13" y2="18" />
                  </svg>
                )}
                {loading || loadingMore ? "Filtering…" : "Filter"}
                {activeFilterCount > 0 && (
                  <span
                    style={{
                      backgroundColor: "#3A6ED4",
                      color: "#fff",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {activeFilterCount > 0 && (
                <button
                  onClick={handleClearAllFilters}
                  style={{
                    padding: "9px 18px",
                    borderRadius: "20px",
                    border: "1px solid #dc3545",
                    backgroundColor: "transparent",
                    color: "#dc3545",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* ── Job Cards ── */}
          {loading && jobs && !jobs[0] && <SectionLoader />}
          {!loading && jobs && !jobs[0] && (
            <div
              className="col-12 d-flex flex-column align-items-center justify-content-center"
              style={{ minHeight: "400px", padding: "40px 20px" }}
            >
              <div
                className="d-flex flex-column align-items-center justify-content-center text-center"
                style={{
                  maxWidth: "600px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "16px",
                  padding: "48px 32px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.07)",
                }}
              >
                <div style={{ fontSize: "64px", marginBottom: "24px", opacity: 0.8 }}>🌍</div>
                <h3 style={{ fontSize: "24px", fontWeight: "600", color: "#2c3e50", marginBottom: "16px", lineHeight: "1.4" }}>
                  Your country&apos;s hiring scene starts with you!
                </h3>
                <p style={{ fontSize: "16px", color: "#6c757d", lineHeight: "1.6", marginBottom: "0" }}>
                  Be the first to post a job and put your country on the map. Whether you&apos;re building a team or exploring new opportunities, workbrook has everything you need to get started. Browse remote roles, discover talent across locations, or{" "}
                  <a href="/onboarding" style={{ color: "#0d6efd" }}>
                    Visit our onboarding page
                  </a>{" "}
                  to see how workbrook can work for you.
                </p>
              </div>
            </div>
          )}

          {jobs && jobs[0] && (
            <>
              {jobs.map((job, index) => (
                <div
                  style={{ padding: "10px", maxHeight: "280px" }}
                  key={index.toString()}
                  className="col-12 col-md-6"
                >
                  <JobCard job={job} />
                </div>
              ))}
            </>
          )}

          {/* ── Pagination ── */}
          {totalJobs > 0 && totalPages >= 1 && (
            <div className="container" style={{ marginTop: "5%" }}>
              <div className="row clearfix">
                <div className="mx-auto col-md-2 mt-3 w-75" />

                <div className="mx-auto col-md-2 mt-3 w-75">
                  <CustomButton
                    handleClick={() => handlePageChange(page - 1)}
                    text={loadingMore ? <ButtonLoader /> : "Prev"}
                    disabled={page === 1 || loading}
                  />
                </div>

                <div className="col-md-2 text-center" style={{ marginTop: "2.5%" }}>
                  <p style={{ marginBottom: "4px", fontWeight: "500" }}>
                    Page {page} of {totalPages}
                  </p>
                  {totalJobs > 0 && (
                    <p style={{ fontSize: "12px", color: "#999", marginBottom: "0" }}>
                      {totalJobs} jobs
                    </p>
                  )}
                </div>

                <div className="mx-auto col-md-2 mt-3 w-75">
                  <CustomButton
                    handleClick={() => handlePageChange(page + 1)}
                    text={loadingMore ? <ButtonLoader /> : "Next"}
                    disabled={loading || page === totalPages}
                  />
                </div>

                <div className="mx-auto col-md-2 mt-3 w-75" />
              </div>
            </div>
          )}

          {/* ── Filter Modal ── */}
          {filterModalOpen && (
            <JobFilters
              open={filterModalOpen}
              onClose={() => setFilterModalOpen(false)}
              onApply={handleApplyFilters}
              currentFilters={activeFilters}
              countries={countries}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AllJobs;